import type { FeatureCollection } from 'geojson';
import type { ModuleId } from '../../module-store';
import { wasteApi } from '../../modules/waste/services/waste-api';

export type DataHubSectionId = 'import' | 'export';
export type DataHubFormat = 'CSV' | 'GeoJSON' | 'KML';
export type DataHubOperationAvailability = 'available' | 'coming_soon';
export type DataHubGeometryLabel = 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon';

export interface DataHubActionDefinition {
  kind: 'open-waste-import' | 'open-building-import' | 'download-geojson';
  label: string;
  helperText: string;
  entityType?: string;
  filePrefix?: string;
  fetchData?: (projectId: string) => Promise<FeatureCollection>;
}

export interface DataHubOperationDefinition {
  id: string;
  groupLabel?: string;
  entityLabel: string;
  description: string;
  geometry: readonly DataHubGeometryLabel[];
  formats: readonly DataHubFormat[];
  availability: DataHubOperationAvailability;
  availabilityLabel?: string;
  note?: string;
  action: DataHubActionDefinition | null;
}

export interface DataHubSectionDefinition {
  id: DataHubSectionId;
  title: string;
  description: string;
  coverageNote?: string;
  operations: readonly DataHubOperationDefinition[];
}

export interface DataHubModuleDefinition {
  moduleId: ModuleId;
  label: string;
  statusLabel: string;
  description: string;
  scopeTitle?: string;
  scopeItems?: readonly string[];
  sections: Record<DataHubSectionId, DataHubSectionDefinition>;
}

export const DATA_HUB_MODULE_LABELS: Record<ModuleId, string> = {
  transit: 'Tránsito',
  transport: 'Transporte',
  roadways: 'Vialidades',
  environmental: 'Ambiental',
  infrastructure: 'Infraestructura',
  urban: 'Planificación',
  social: 'Social',
  services: 'Servicios',
  safety: 'Seguridad',
  waste: 'Residuos',
  'urban-network': 'Red Urbana',
  mobility: 'Movilidad',
  'urban-inventory': 'Inventario Urbano',
};

export const DATA_HUB_MODULE_CONFIGS: Partial<Record<ModuleId, DataHubModuleDefinition>> = {
  waste: {
    moduleId: 'waste',
    label: 'Residuos',
    statusLabel: 'Integración inicial activa',
    description:
      'Operaciones de intercambio publicadas hoy para el módulo Waste del proyecto activo.',
    scopeTitle: 'Alcance del módulo',
    scopeItems: [
      'La importación en Residuos opera hoy solo sobre entidades puntuales.',
      'No soporta importación de líneas en este módulo.',
      'No soporta importación de polígonos en este módulo.',
      'Zonas y Rutas están disponibles solo para exportación.',
      'Las capas territoriales poligonales se gestionan desde Planificación.',
    ],
    sections: {
      import: {
        id: 'import',
        title: 'Importar en Residuos',
        description:
          'Cada operación abre el flujo real de Waste con la entidad correspondiente ya enfocada.',
        coverageNote:
          'Capacidades publicadas hoy para importación en Residuos: Contenedores y Depósitos.',
        operations: [
          {
            id: 'waste-import-containers',
            entityLabel: 'Contenedores',
            description:
              'Carga contenedores con ubicación y atributos operativos desde archivos puntuales.',
            geometry: ['Point'],
            formats: ['CSV', 'GeoJSON', 'KML'],
            availability: 'available',
            availabilityLabel: 'Disponible ahora',
            note: 'Abre el wizard de Waste con Contenedores preseleccionado.',
            action: {
              kind: 'open-waste-import',
              label: 'Abrir importador',
              helperText: 'Continúa en el wizard real de Waste.',
              entityType: 'waste_container',
            },
          },
          {
            id: 'waste-import-depots',
            entityLabel: 'Depósitos',
            description:
              'Importa bases, puntos operativos y sus atributos espaciales desde archivos compatibles.',
            geometry: ['Point'],
            formats: ['CSV', 'GeoJSON', 'KML'],
            availability: 'available',
            availabilityLabel: 'Disponible ahora',
            note: 'Abre el wizard de Waste con Depósitos preseleccionado.',
            action: {
              kind: 'open-waste-import',
              label: 'Abrir importador',
              helperText: 'Continúa en el wizard real de Waste.',
              entityType: 'waste_depot',
            },
          },
        ],
      },
      export: {
        id: 'export',
        title: 'Exportar desde Residuos',
        description:
          'Las operaciones conectadas descargan directamente la información vigente del proyecto activo.',
        coverageNote:
          'Capacidades publicadas hoy para exportación en Residuos: Contenedores, Depósitos, Zonas y Rutas.',
        operations: [
          {
            id: 'waste-export-containers',
            entityLabel: 'Contenedores',
            description:
              'Descarga la capa actual de contenedores con geometría y atributos disponibles.',
            geometry: ['Point'],
            formats: ['GeoJSON'],
            availability: 'available',
            availabilityLabel: 'Descarga directa',
            note: 'Usa el endpoint activo del módulo Waste.',
            action: {
              kind: 'download-geojson',
              label: 'Descargar GeoJSON',
              helperText: 'Genera un archivo listo para usar fuera de WESTEER.',
              filePrefix: 'contenedores',
              fetchData: (projectId) => wasteApi.containers.getGeoJSON(projectId),
            },
          },
          {
            id: 'waste-export-depots',
            entityLabel: 'Depósitos',
            description:
              'Exporta bases y puntos operativos con la geometría vigente del proyecto.',
            geometry: ['Point'],
            formats: ['GeoJSON'],
            availability: 'available',
            availabilityLabel: 'Descarga directa',
            note: 'Usa el endpoint activo del módulo Waste.',
            action: {
              kind: 'download-geojson',
              label: 'Descargar GeoJSON',
              helperText: 'Genera un archivo listo para usar fuera de WESTEER.',
              filePrefix: 'depositos',
              fetchData: (projectId) => wasteApi.depots.getGeoJSON(projectId),
            },
          },
          {
            id: 'waste-export-zones',
            entityLabel: 'Zonas',
            description:
              'Descarga las zonas de cobertura del proyecto en un archivo espacial estándar.',
            geometry: ['Polygon'],
            formats: ['GeoJSON'],
            availability: 'available',
            availabilityLabel: 'Descarga directa',
            note: 'Usa el endpoint activo del módulo Waste.',
            action: {
              kind: 'download-geojson',
              label: 'Descargar GeoJSON',
              helperText: 'Genera un archivo listo para usar fuera de WESTEER.',
              filePrefix: 'zonas',
              fetchData: (projectId) => wasteApi.zones.getGeoJSON(projectId),
            },
          },
          {
            id: 'waste-export-routes',
            entityLabel: 'Rutas',
            description:
              'Exporta la capa actual de rutas del módulo con la geometría disponible.',
            geometry: ['LineString'],
            formats: ['GeoJSON'],
            availability: 'available',
            availabilityLabel: 'Descarga directa',
            note: 'Usa el endpoint activo del módulo Waste.',
            action: {
              kind: 'download-geojson',
              label: 'Descargar GeoJSON',
              helperText: 'Genera un archivo listo para usar fuera de WESTEER.',
              filePrefix: 'rutas',
              fetchData: (projectId) => wasteApi.routes.getGeoJSON(projectId),
            },
          },
        ],
      },
    },
  },
  urban: {
    moduleId: 'urban',
    label: 'Planificación',
    statusLabel: 'Edilicio + Capas territoriales',
    description:
      'Entrada de producto para capas territoriales y datos edilicios del proyecto, alineada con el módulo Urban.',
    scopeTitle: 'Alcance del módulo',
    scopeItems: [
      'Importación edilicia por sesión: CSV/CSV.GZ con mapping configurable.',
      'Las capas territoriales poligonales se gestionan desde Planificación.',
      'La Fase 1 visible para capas se limita a Zonificación.',
    ],
    sections: {
      import: {
        id: 'import',
        title: 'Importar en Planificación',
        description:
          'Datos edilicios y capas territoriales del proyecto. El flujo edilicio crea BuildingCentroid + BuildingAttributes en sesiones auditadas.',
        coverageNote:
          'Edilicio disponible: CSV/CSV.GZ con mapping configurable. Zonificación en preparación.',
        operations: [
          {
            id: 'urban-import-buildings',
            groupLabel: 'Edilicio',
            entityLabel: 'Edificios (CSV)',
            description:
              'Importa centroides edilicios desde archivos CSV o CSV.GZ con mapping de columnas configurable, sugerencias automáticas y validación de calidad de datos.',
            geometry: ['Point'],
            formats: ['CSV'],
            availability: 'available',
            availabilityLabel: 'Disponible ahora',
            note: 'Abre el wizard de importación edilicia por sesión (E3-D).',
            action: {
              kind: 'open-building-import',
              label: 'Abrir importador',
              helperText: 'Flujo de 4 pasos: Fuente → Archivo → Mapping → Validar e importar.',
            },
          },
          {
            id: 'urban-import-zoning',
            groupLabel: 'Capas territoriales',
            entityLabel: 'Zonificación',
            description:
              'Primera entrada poligonal de Urban para cargar zonificación del proyecto con semántica explícita.',
            geometry: ['Polygon', 'MultiPolygon'],
            formats: ['GeoJSON', 'KML'],
            availability: 'coming_soon',
            availabilityLabel: 'En preparación',
            note:
              'Campos sugeridos para mapping inicial: code, name y category; sin fijar todavía el schema definitivo.',
            action: null,
          },
        ],
      },
      export: {
        id: 'export',
        title: 'Exportar desde Planificación',
        description:
          'Las capas territoriales publicadas desde Urban se descargarán por tipo de capa y formato espacial.',
        coverageNote:
          'La exportación inicial prevista para Zonificación será GeoJSON.',
        operations: [
          {
            id: 'urban-export-zoning',
            groupLabel: 'Capas territoriales',
            entityLabel: 'Zonificación',
            description:
              'Salida prevista para descargar la capa de zonificación del proyecto en un formato espacial interoperable.',
            geometry: ['Polygon', 'MultiPolygon'],
            formats: ['GeoJSON'],
            availability: 'coming_soon',
            availabilityLabel: 'En preparación',
            note:
              'La ficha deja visible la capacidad objetivo sin prometer todavía un flujo conectado.',
            action: null,
          },
        ],
      },
    },
  },
};
